import { FunctionComponent, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { format } from "date-fns";
import { useQuery } from "@apollo/client";
import dashIcon from "../../assets/dashboard-svgrepo-com.svg";
import backArrow from "../../assets/task/back_arrow.svg";
import BreadCumb from "../../components/common/Breadcrumb";
import DateSelector from "../../components/common/DateSelector";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import { showError } from "../../components/common/ToastNotification";
import { PODS_GOAL_SUMMARY } from "../../api/query";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const ACTION_TYPE_CLASS: Record<string, string> = {
  Bug: "lightred-statusbg-card",
  Enhancement: "lightgreen-statusbg-card",
  "Training Issue": "lightyellow-statusbg-card",
  "Follow-up": "lightgray-statusbg-card",
  "Release Note": "lightblue-statusbg-card",
};

const GoalsDash: FunctionComponent = () => {
  const { userInfo } = useAuth();
  const agencyId = userInfo?.agencies?.id;
  const navigate = useNavigate();
  const today = format(new Date(), "yyyy-MM-dd");

  const [startDate, setStartDate] = useState<string>(today);
  const [endDate, setEndDate] = useState<string>(today);

  const { control } = useForm<any>();

  const { data, loading, error } = useQuery(PODS_GOAL_SUMMARY, {
    fetchPolicy: "no-cache",
    variables: {
      agency_id: agencyId ? parseInt(agencyId) : undefined,
      start_date: startDate,
      end_date: endDate,
      today: today,
    },
    skip: !agencyId || !startDate || !endDate,
    onError: (err) => showError(err.message),
  });

  const summary = data?.pods_goal_summary;
  const pods: any[] = summary?.pods ?? [];

  console.log(data, "data");

  return (
    <section
      id="cts-respon-p-dv"
      className="w-full relative overflow-hidden flex flex-row items-start gap-6 justify-center flex-wrap content-start py-40 px-20 mq991:p-8 mq575:p-4 box-border text-left text-base text-gray font-manrope"
    >
      {/* Breadcrumb + Header */}
      <div className="self-stretch grid flex-row items-center justify-start w-full gap-[5px] max-w-full">
        <BreadCumb />
        <div className="self-stretch flex flex-row items-center justify-start gap-[5px] text-2xl">
          <img
            className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer top-[0px] dark-white-img"
            alt=""
            src={backArrow}
            onClick={() => navigate(-1)}
          />
          <h3 className="m-0 flex-1 relative text-inherit font-bold font-inherit inline-block max-w-[calc(100%_-_29px)] mq450:text-base dark:text-white">
            Goal portfolio across all pods
          </h3>
        </div>
      </div>

      {/* Top panel — date filters + summary cards */}
      <div className="goalsdash-main-dv flex self-stretch flex-col min-w-full m-0 rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
        {/* Title row */}
        <div className="topnav-dash-item-dv flex gap-4 items-center py-1 px-2 min-w-full mq825:grid">
          <div className="flex gap-2 items-center">
            <img src={dashIcon} className="w-5 h-5 object-cover" alt="" />
            <h5 className="my-0 text-xl-dv self-stretch relative text-lg dark:text-white">
              Goal portfolio · all pods
            </h5>
          </div>
        </div>
        {/* <div onClick={()=>navigate("/create_pod_goal")}>Add Goal</div> */}

        {/* Date filters */}
        <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 items-end gap-[16px] min-w-full">
          <Controller
            name="start_date"
            control={control}
            render={({ field }) => (
              <DateSelector
                label="Start Date"
                selectedDate={startDate}
                onChange={(date: any) => {
                  field.onChange(date);
                  if (date) {
                    setStartDate(format(new Date(date), "yyyy-MM-dd"));
                    setEndDate("");
                  }
                }}
                nothideParsedValue={true}
                blueIcon={true}
              />
            )}
          />
          <Controller
            name="end_date"
            control={control}
            render={({ field }) => (
              <DateSelector
                label="End Date"
                selectedDate={endDate}
                onChange={(date: any) => {
                  if (startDate && new Date(date) < new Date(startDate)) {
                    showError("End date cannot be earlier than start date.");
                    return;
                  }
                  field.onChange(date);

                  if (date) {
                    setEndDate(format(new Date(date), "yyyy-MM-dd"));
                  }
                }}
                nothideParsedValue={true}
                blueIcon={true}
              />
            )}
          />
        </div>

        {/* Summary cards */}
        {loading ? (
          <div className="w-full">
            <SkeletonLoader count={3} height="h-16" className="mb-2" />
          </div>
        ) : (
          <div className="grid grid-cols-4 mq1400:grid-4 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[16px] min-w-full">
            <div className="goaldash-card-item-dv p-4 grid gap-1">
              <p className="my-0">Open goals</p>
              <h5 className="my-0 text-xl-dv self-stretch relative text-lg dark:text-white">
                {summary?.total_open_goals ?? 0}
              </h5>
            </div>
            <div className="goaldash-card-item-dv p-4 grid gap-1">
              <p className="my-0">Goals ending today</p>
              <h5 className="my-0 text-xl-dv self-stretch relative text-lg dark:text-white">
                {summary?.goals_ending_today ?? 0}
              </h5>
            </div>
            <div className="goaldash-card-item-dv p-4 grid gap-1">
              <p className="my-0">Delayed goals</p>
              <h5 className="my-0 text-xl-dv self-stretch relative text-lg dark:text-white">
                {summary?.delayed_goals ?? 0}
              </h5>
            </div>
          </div>
        )}
      </div>

      {/* Pods panel */}
      <div className="goalsdash-main-dv flex self-stretch flex-col min-w-full m-0 rounded-radius-radius-lg bg-surface-bg-blue darkborder-b-remove items-start justify-start p-8 mq575:p-3 box-border gap-spacing-lg max-w-full mq825:pt-5 mq825:pb-5 mq825:box-border mq1425:pt-[21px] mq1425:pb-[21px] mq1425:box-border">
        {loading ? (
          <div className="w-full">
            <SkeletonLoader count={6} height="h-28" className="mb-3" />
          </div>
        ) : error ? (
          <div className="self-stretch rounded-lg overflow-hidden flex flex-col items-center justify-center py-[60px] bg-slate-100 dark:bg-slate-700">
            <p className="text-sm text-red-500">
              Failed to load data. Please try again.
            </p>
          </div>
        ) : pods.length === 0 ? (
          <div className="self-stretch rounded-lg overflow-hidden flex flex-col items-center justify-center py-[60px] bg-slate-100 dark:bg-slate-700">
            <p className="text-sm dark:text-white">No data available</p>
          </div>
        ) : (
          <>
            <div className="flex self-stretch w-full">
              <p className="my-0 text-sm dark:text-white">
                Pods · {pods.length} pod{pods.length !== 1 ? "s" : ""}
              </p>
            </div>

            <div className="grid grid-cols-3 mq1400:grid-3 mq1275:grid-cols-3 mq950:grid-cols-2 mq575:grid-cols-1 gap-[16px] min-w-full">
              {pods.map((pod: any, idx: number) => (
                <div
                  key={pod.pod_id ?? idx}
                  className="goaldash-card-item-dv p-4 grid gap-4"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate("/goals_dash_pod_detail", {
                      state: {
                        pod_id: pod.pod_id,
                        pod_name: pod.pod_name,
                        pod_type: pod.pod_type,
                      },
                    });
                  }}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h5 className="my-0 text-xl-dv self-stretch relative text-lg dark:text-white">
                      {idx + 1}. {pod.pod_name}
                    </h5>
                    <span className="text-xs font-semibold px-2 py-1 rounded-full dash-card-right bg-blue-100 text-blue-700 shrink-0">
                      {pod.open_goal_count} open
                    </span>
                  </div>

                  {/* {pod.pod_type && (
                    <p className="my-0 text-xs text-gray-500 dark:text-gray-400">{pod.pod_type}</p>
                  )} */}

                  {Array.isArray(pod.action_type_counts) &&
                    pod.action_type_counts.length > 0 && (
                      <div className="flex gap-2 flex-wrap taggoal-ui-list">
                        {pod.action_type_counts.map((atc: any, i: number) => (
                          <span
                            key={i}
                            className={
                              ACTION_TYPE_CLASS[atc.action_type] ??
                              "lightgray-statusbg-card"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate("/goals_dash_pod_detail", {
                                state: {
                                  pod_id: pod.pod_id,
                                  pod_name: pod.pod_name,
                                  pod_type: pod.pod_type,
                                  action_type: atc?.action_type,
                                },
                              });
                            }}
                          >
                            {atc.count} {atc.action_type}
                          </span>
                        ))}
                      </div>
                    )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default GoalsDash;
